import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Stack,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import SpeedIcon from '@mui/icons-material/Speed';
import BuildIcon from '@mui/icons-material/Build';
import BarChartIcon from '@mui/icons-material/BarChart';
import SecurityIcon from '@mui/icons-material/Security';
import GroupIcon from '@mui/icons-material/Group';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const brandBlue = '#0B60B1';

const features = [
  {
    icon: <SpeedIcon sx={{ fontSize: 40, color: brandBlue }} />,
    title: 'Campaign Optimization',
    description: 'Expert setup and optimization of your Smartlead campaigns for maximum performance and deliverability.'
  },
  {
    icon: <BuildIcon sx={{ fontSize: 40, color: brandBlue }} />,
    title: 'Technical Setup',
    description: 'Professional configuration of your sending infrastructure, domains, and email authentication.'
  },
  {
    icon: <BarChartIcon sx={{ fontSize: 40, color: brandBlue }} />,
    title: 'Performance Analytics',
    description: 'In-depth analysis and reporting of campaign metrics, with actionable insights for improvement.'
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 40, color: brandBlue }} />,
    title: 'Deliverability Management',
    description: 'Proactive monitoring and optimization of your sender reputation and email deliverability.'
  },
  {
    icon: <GroupIcon sx={{ fontSize: 40, color: brandBlue }} />,
    title: 'Expert Support',
    description: 'Dedicated team of Smartlead experts with experience managing 5000+ successful campaigns.'
  }
];

const stats = [
  { label: 'Campaigns Managed', value: '5000+' },
  { label: 'Average Open Rate', value: '80%' },
  { label: 'Average Reply Rate', value: '15%' },
  { label: 'Client Success Rate', value: '95%' }
];

const services = [
  'Campaign Strategy Development',
  'Email Template Optimization',
  'Deliverability Monitoring',
  'Performance Analysis',
  'A/B Testing',
  'Lead Generation Setup',
  'Domain Warm-up',
  'Technical Configuration',
  'Team Training',
  'Ongoing Support'
];

export default function SmartleadPage() {
  return (
    <Box sx={{ bgcolor: '#f7fafd', minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box sx={{ bgcolor: brandBlue, color: '#fff', py: { xs: 6, md: 10 }, position: 'relative' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={12}>
              <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
                <Typography variant="h3" fontWeight={700} gutterBottom>
                  Smartlead Consulting
                </Typography>
                <Typography variant="h5" sx={{ mb: 4, fontWeight: 400, mx: 'auto' }}>
                  Maximize your cold email success with our expert Smartlead consulting services
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    href="/contact"
                    sx={{ fontWeight: 600 }}
                  >
                    Get Started
                  </Button>
                  <Button
                    variant="outlined"
                    color="inherit"
                    size="large"
                    href="#services"
                    sx={{ fontWeight: 600, borderColor: '#fff' }}
                  >
                    Our Services
                  </Button>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={3} justifyContent="center">
          {stats.map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Paper elevation={0} sx={{ p: 3, textAlign: 'center', bgcolor: 'white' }}>
                <Typography variant="h4" color="primary" fontWeight={700}>
                  {stat.value}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  {stat.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" fontWeight={700} textAlign="center" gutterBottom>
          Why Choose Our Smartlead Expertise
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" textAlign="center" sx={{ mb: 6 }}>
          Leverage our experience of managing over 5000 successful campaigns
        </Typography>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card elevation={0} sx={{ height: '100%', bgcolor: 'transparent' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    {feature.icon}
                    <Box>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        {feature.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Services Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" fontWeight={700} textAlign="center" gutterBottom>
          Our Services
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" textAlign="center" sx={{ mb: 6 }}>
          Comprehensive Smartlead consulting services to boost your campaign performance
        </Typography>
        <Grid container spacing={2}>
          {services.map((service, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  bgcolor: 'white',
                  '&:hover': { transform: 'translateY(-4px)', transition: 'transform 0.2s' }
                }}
              >
                <CheckCircleIcon color="primary" />
                <Typography variant="subtitle1" fontWeight={500}>
                  {service}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            bgcolor: 'white',
            borderRadius: 4
          }}
        >
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Ready to Maximize Your Smartlead Success?
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
            Let our team of experts help you achieve outstanding campaign results
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            href="/contact"
            sx={{ fontWeight: 600 }}
          >
            Schedule a Consultation
          </Button>
        </Paper>
      </Container>
    </Box>
  );
} 